
import React, { useState, useRef, useEffect } from 'react';

const AREAS = [
  'Bedline', 'TV unit', 'Curtains', 'Coffee maker', 'Fridge', 
  'Couch', 'Iron unit', 'Towels', 'Dental', 'Toiletries', 'Neatness'
];

export default function RoomChecklist({ roomData, onComplete }) {
  const [photos, setPhotos] = useState({});
  const [currentArea, setCurrentArea] = useState(AREAS[0]);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [autoCompleted, setAutoCompleted] = useState(false);
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState({});

  const handleCheckIn = () => {
    setScanSuccess(true);
  };

  // Function to upload image to server/storage
  const uploadImage = async (file, area) => {
    // Simulate upload progress
    setUploadProgress(prev => ({ ...prev, [area]: 0 }));
    
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[area] || 0;
          if (current >= 100) {
            clearInterval(interval);
            return prev;
          }
          return { ...prev, [area]: current + 10 };
        });
      }, 100);

      // Simulate API call - Replace with actual upload logic
      setTimeout(() => {
        clearInterval(interval);
        setUploadProgress(prev => ({ ...prev, [area]: 100 }));
        
        // In real implementation, you would upload to your server:
        // const formData = new FormData();
        // formData.append('image', file);
        // formData.append('area', area);
        // formData.append('roomId', roomData?.roomId);
        // const response = await fetch('/api/upload-evidence', { method: 'POST', body: formData });
        // const data = await response.json();
        // resolve(data.url);
        
        // For demo, create local URL
        const photoUrl = URL.createObjectURL(file);
        resolve(photoUrl);
      }, 1000);
    });
  };

  // Auto capture and upload function with actual file upload
  const autoCaptureAndUpload = async (area) => {
    setIsAutoProcessing(true);
    
    // Trigger file input for camera/photo selection
    fileInputRef.current.click();
    fileInputRef.current.onchange = async (e) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        
        // Show toast for upload start
        showToast(`📤 Uploading evidence for ${area}...`, 'info');
        
        try {
          // Upload the image
          const uploadedUrl = await uploadImage(file, area);
          
          // Store the uploaded image URL
          setPhotos(prev => ({ ...prev, [area]: uploadedUrl }));
          
          // Show success toast
          showToast(`✅ ${area} automatically checked and evidence uploaded!`, 'success');
          
          // Clear the file input
          e.target.value = '';
        } catch (error) {
          console.error('Upload failed:', error);
          showToast(`❌ Failed to upload evidence for ${area}`, 'error');
        } finally {
          setIsAutoProcessing(false);
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[area];
            return newProgress;
          });
        }
      } else {
        setIsAutoProcessing(false);
      }
    };
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
    toast.className = `fixed top-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in`;
    toast.innerHTML = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const handleAreaClick = (area) => {
    setCurrentArea(area);
    // Auto check and upload evidence when clicking on any tab button
    if (!photos[area] && scanSuccess && !isAutoProcessing) {
      autoCaptureAndUpload(area);
    }
  };

  const handleRetakePhoto = (area) => {
    if (!isAutoProcessing) {
      autoCaptureAndUpload(area);
    }
  };

  const isComplete = () => {
    return AREAS.every(area => photos[area] !== undefined);
  };

  useEffect(() => {
    if (isComplete() && !autoCompleted && scanSuccess) {
      setAutoCompleted(true);
      setTimeout(() => {
        onComplete(photos);
      }, 1000);
    }
  }, [photos, autoCompleted, scanSuccess, onComplete]);

  useEffect(() => {
    if (photos[currentArea] && currentArea !== AREAS[AREAS.length - 1]) {
      const nextIndex = AREAS.indexOf(currentArea) + 1;
      setCurrentArea(AREAS[nextIndex]);
    }
  }, [photos, currentArea]);

  const completedCount = Object.keys(photos).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Room Inspection
              </h1>
              <p className="text-gray-500 mt-1">Click any tab to upload photo evidence</p>
            </div>
            <div className="bg-indigo-100 rounded-xl px-4 py-2">
              <span className="text-indigo-700 font-semibold">
                Room: {roomData?.roomId || 'Not allocated'}
              </span>
            </div>
          </div>
        </div>

        {/* QR Check-in */}
        {!scanSuccess ? (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 text-center">
            <div className="w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Scan QR Code to Begin</h2>
            <p className="text-gray-500 mb-6">Check-in to start the inspection process</p>
            <button onClick={handleCheckIn} className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105">
              📷 Scan QR Code
            </button>
          </div>
        ) : (
          <>
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-800">Checked in successfully at {new Date().toLocaleTimeString()}</span>
                </div>
                <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-sm">
                  📸 Photo upload mode: ACTIVE
                </div>
              </div>
            </div>

            {/* Quick Navigation - Click any tab to upload photo */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-700">Quick Navigation</h3>
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">Click tab → Upload photo evidence</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {AREAS.map(area => (
                  <button
                    key={area}
                    onClick={() => handleAreaClick(area)}
                    disabled={isAutoProcessing && currentArea === area}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      currentArea === area 
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md' 
                        : photos[area] 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } ${isAutoProcessing && currentArea === area ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {area} 
                    {photos[area] && ' ✓'}
                    {uploadProgress[area] > 0 && uploadProgress[area] < 100 && ' 📤'}
                    {isAutoProcessing && currentArea === area && ' 📸'}
                  </button>
                ))}
              </div>
            </div>

            {/* Current Area */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">{currentArea}</h2>
                {photos[currentArea] && (
                  <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                    <span>✓</span> Photo uploaded
                  </span>
                )}
              </div>

              <div className="space-y-6">
                <div className={`rounded-xl p-4 ${photos[currentArea] ? 'bg-green-50' : 'bg-blue-50'}`}>
                  <p className="text-gray-600">
                    {photos[currentArea] 
                      ? "✅ Photo evidence uploaded successfully" 
                      : uploadProgress[currentArea] && uploadProgress[currentArea] < 100
                      ? `📤 Uploading photo... ${uploadProgress[currentArea]}%`
                      : isAutoProcessing
                      ? "📸 Opening camera to capture photo evidence..."
                      : "📸 Click the tab above to upload photo evidence for this area"}
                  </p>
                  {uploadProgress[currentArea] > 0 && uploadProgress[currentArea] < 100 && (
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-300"
                        style={{ width: `${uploadProgress[currentArea]}%` }}
                      />
                    </div>
                  )}
                </div>

                <div>
                  {photos[currentArea] ? (
                    <div className="space-y-3">
                      <img 
                        src={photos[currentArea]} 
                        alt={currentArea} 
                        className="w-48 h-48 object-cover rounded-xl border-2 border-green-300 shadow-md" 
                      />
                      <button 
                        onClick={() => handleRetakePhoto(currentArea)} 
                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                        disabled={isAutoProcessing}
                      >
                        {isAutoProcessing ? '📸 Uploading...' : '📷 Upload New Photo'}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center p-8 bg-gray-50 rounded-xl">
                      <div className="text-6xl mb-3">📸</div>
                      <p className="text-gray-500 mb-3">No photo evidence uploaded yet</p>
                      <button 
                        onClick={() => handleAreaClick(currentArea)} 
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                        disabled={isAutoProcessing}
                      >
                        {isAutoProcessing ? '📸 Opening Camera...' : '📸 Upload Photo Evidence'}
                      </button>
                      <p className="text-xs text-gray-400 mt-3">
                                        Click to take a photo or select from gallery
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700">Inspection Progress</h3>
                <span className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded">
                  {completedCount} of {AREAS.length} uploaded
                </span>
              </div>
              
              <div className="mb-4">
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500 rounded-full"
                    style={{ width: `${(completedCount / AREAS.length) * 100}%` }}
                  />
                </div>
                <p className="text-center text-gray-600 mt-2 font-semibold">
                  {completedCount} of {AREAS.length} areas completed
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {AREAS.map(area => (
                  <div 
                    key={area} 
                    className={`flex items-center space-x-2 text-sm p-2 rounded cursor-pointer hover:bg-gray-50 transition-all ${
                      photos[area] ? 'bg-green-50' : ''
                    }`}
                    onClick={() => handleAreaClick(area)}
                  >
                    <span>{photos[area] ? '✅' : uploadProgress[area] ? '📤' : '◻️'}</span>
                    <span className={photos[area] ? 'text-green-700 font-medium' : 'text-gray-700'}>
                      {area}
                    </span>
                    {photos[area] && <span className="text-xs text-green-500 ml-auto">📷</span>}
                    {uploadProgress[area] > 0 && uploadProgress[area] < 100 && (
                      <span className="text-xs text-blue-500 ml-auto">{uploadProgress[area]}%</span>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => onComplete(photos)}
                disabled={!isComplete()}
                className={`w-full mt-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                  isComplete() 
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:shadow-lg transform hover:scale-105' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isComplete() 
                  ? '✓ All Photos Uploaded! Complete Inspection →' 
                  : `📸 Upload ${AREAS.length - completedCount} more photo(s) to continue`}
              </button>

              {!isComplete() && (
                <p className="text-center text-xs text-gray-400 mt-3">
                  💡 Tip: Click on any area tab to upload photo evidence
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Hidden file input for camera/gallery */}
      <input 
        type="file" 
        ref={fileInputRef} 
        accept="image/*" 
        capture="environment" 
        className="hidden" 
      />

      {/* Success Toast */}
      {autoCompleted && (
        <div className="fixed bottom-6 right-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl shadow-2xl animate-bounce">
          🎉 All photos uploaded! Redirecting...
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce {
          animation: bounce 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
}